import { MouseEvent, startTransition, useCallback, useRef } from 'react';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { Header, SortDirection, type Row as TableRowType, flexRender, noop } from '@tanstack/react-table';
import { rgbaColors } from '@karnott/colors';
import { ChevronIcon, KIcon } from '@karnott/icons';
import {
  BodyCell,
  FooterCell,
  HeaderCell,
  HeaderTitle,
  HeaderWithSort,
  SortButtons,
  TableContainer,
  TableElement,
  TableRow,
  Tbody,
  Tfoot,
  Thead,
} from '../components';
import { DataTableProps, RowId, RowShape } from '../types';
import { useTable, useTotalRow, useVirtualRows } from './hooks';

type ColumnHeaderProps<Row extends RowShape> = {
  header: Header<Row, unknown>;
  Icon?: KIcon;
  background?: string;
  multiline: boolean;
};

function ColumnHeader<Row extends RowShape>({ header, Icon, background, multiline }: ColumnHeaderProps<Row>) {
  const canSort = header.column.getCanSort();
  const isSorted = header.column.getIsSorted();
  const headerTitle = useRef<HTMLSpanElement>(null);
  const onClick = useCallback(
    (e: MouseEvent<HTMLTableCellElement>) => {
      startTransition(() => header.column.getToggleSortingHandler()?.(e));
    },
    [header.column],
  );

  const iconColorFor = (order: SortDirection) =>
    rgbaColors(background ? 'white' : 'grey', 600, isSorted === order ? 1 : 0.6) || undefined;

  const width = header.getContext().column.columnDef.meta?.width;

  return (
    <HeaderCell
      title={(headerTitle.current?.innerText ? headerTitle.current?.textContent : null) || undefined}
      canSort={canSort}
      onClick={onClick}
      background={background}
      $width={width}
    >
      <HeaderWithSort>
        {header.index === 0 && Icon ? <Icon size={16} color="currentColor" /> : null}
        <HeaderTitle ref={headerTitle} multiline={multiline}>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </HeaderTitle>
        {canSort ? (
          <SortButtons>
            <ChevronIcon size={18} color={iconColorFor('asc')} />
            <ChevronIcon size={18} color={iconColorFor('desc')} />
          </SortButtons>
        ) : null}
      </HeaderWithSort>
    </HeaderCell>
  );
}

type VirtualPaddingProps = {
  size: number;
};

function VirtualPadding({ size }: VirtualPaddingProps) {
  return size > 0 ? (
    <tr>
      <td style={{ height: size }} />
    </tr>
  ) : null;
}

type RowProps<Row extends RowShape> = {
  row: TableRowType<Row>;
  index: number;
  measureElement: (node: Element | null) => void;
  highlightOnHover: boolean;
  onHover: ((rowId: RowId | null) => void) | undefined;
  enableDragAndDrop: boolean;
  containerElement: HTMLElement | null;
};

function Row<Row extends RowShape>({
  row,
  index,
  measureElement,
  highlightOnHover,
  onHover,
  enableDragAndDrop,
  containerElement,
}: RowProps<Row>) {
  const { ref: dragRef } = useSortable({
    id: row.id,
    index,
    disabled: !enableDragAndDrop,
    modifiers: [RestrictToElement.configure({ element: containerElement })],
  });
  const combinedRefCB = useCallback(
    (node: Element | null) => {
      measureElement(node);
      dragRef(enableDragAndDrop ? node : null);
    },
    [dragRef, enableDragAndDrop, measureElement],
  );

  return (
    <TableRow
      data-index={index}
      highlightOnHover={highlightOnHover}
      onMouseEnter={() => row && onHover?.(row.id)}
      onMouseLeave={() => row && onHover?.(null)}
      ref={combinedRefCB}
      role="row"
    >
      {row?.getVisibleCells().map((cell) => (
        <BodyCell key={cell.id} className={cell.column.columnDef.meta?.displayOnHover ? 'cell_on_hover' : ''}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </BodyCell>
      ))}
    </TableRow>
  );
}

const empty: never[] = [];

/** Table to display long lists of data efficiently, with support for sorting and automatic total row. */
export function DataTable<Row extends RowShape>({
  columns = empty,
  rows = empty,
  sortAll = false,
  displayTotal = false,
  selection,
  onSelectionChange = noop,
  onRowHover,
  Icon,
  headerColor,
  highlightOnHover = true,
  multilineHeaders = false,
  disableVirtualization = false,
  draggableRows = false,
  disableDragOnRowIndex,
  onRowDrop = noop,
}: DataTableProps<Row>) {
  /* Table initialization */
  const { table, columnDefs } = useTable({
    columns,
    rows,
    selection,
    onSelectionChange,
    sortAll,
  });
  const { rows: tableRows } = table.getRowModel();

  /* Virtualization */
  const { setScrollElement, virtualRows, paddingBottom, paddingTop, measureElement } = useVirtualRows({
    rows: tableRows,
  });

  /* Total footer cells */
  const totalCells = useTotalRow({ rows: tableRows, columnDefs, displayTotal });

  const hasDefinedWidth = columns.some((c) => c?.width != undefined);

  const tbodyElement = useRef<HTMLTableSectionElement>(null);

  return (
    <TableContainer ref={setScrollElement}>
      <TableElement fixedLayout={hasDefinedWidth}>
        <Thead background={headerColor}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <ColumnHeader
                  key={header.id}
                  header={header}
                  Icon={Icon}
                  background={headerColor}
                  multiline={multilineHeaders}
                />
              ))}
            </tr>
          ))}
        </Thead>

        <Tbody ref={tbodyElement}>
          <DragDropProvider
            modifiers={[RestrictToVerticalAxis]}
            onDragEnd={(e) => onRowDrop(move(tableRows, e).map((r) => r.id))}
          >
            {disableVirtualization ? (
              tableRows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  index={row.index}
                  measureElement={measureElement}
                  highlightOnHover={highlightOnHover}
                  onHover={onRowHover}
                  enableDragAndDrop={
                    draggableRows &&
                    (disableDragOnRowIndex !== undefined ? disableDragOnRowIndex.includes(row.index) === false : true)
                  }
                  containerElement={tbodyElement.current}
                />
              ))
            ) : (
              <>
                <VirtualPadding size={paddingTop} />

                {virtualRows.map((virtualRow) => (
                  <Row
                    key={virtualRow.key}
                    row={tableRows.at(virtualRow.index)!}
                    index={virtualRow.index}
                    measureElement={measureElement}
                    highlightOnHover={highlightOnHover}
                    onHover={onRowHover}
                    enableDragAndDrop={
                      draggableRows &&
                      (disableDragOnRowIndex !== undefined
                        ? disableDragOnRowIndex.includes(virtualRow.index) === false
                        : true)
                    }
                    containerElement={tbodyElement.current}
                  />
                ))}

                <VirtualPadding size={paddingBottom} />
              </>
            )}
          </DragDropProvider>
        </Tbody>

        {displayTotal ? (
          <Tfoot>
            <tr>
              <FooterCell as="th">Total</FooterCell>
              {totalCells.map((cell, i) => {
                const columnDef = table.getAllColumns().at(i + 1)!.columnDef;
                const renderFn = cell
                  ? columnDefs.find((col) =>
                      'accessorKey' in col && 'accessorKey' in columnDef
                        ? col.accessorKey === columnDef.accessorKey
                        : false,
                    )?.cell || null
                  : null;
                return (
                  <FooterCell
                    key={
                      columnDef.id ||
                      ('accessorKey' in columnDef && typeof columnDef.accessorKey === 'string'
                        ? columnDef.accessorKey
                        : undefined)
                    }
                  >
                    {typeof renderFn === 'string' || cell === null
                      ? null
                      : renderFn?.({
                          // @ts-expect-error This is a way to render a custom value using the same formatting as the column
                          getValue: () => cell,
                        })}
                  </FooterCell>
                );
              })}
            </tr>
          </Tfoot>
        ) : null}
      </TableElement>
    </TableContainer>
  );
}
